import { marked, type Tokens } from 'marked';
import { abschnittsAnfaenge } from './markdown-segmente.js';

/**
 * Die Oberflaeche arbeitet in BLOECKEN (Ueberschrift, Absatz, Liste, ...), n8n in
 * ABSCHNITTEN (an Ueberschriften geschnitten). Diese Datei liefert nur die Bloecke; welcher
 * Abschnitt einen Block enthaelt, ergibt sich ueber seinen Offset (`abschnitte.ts`).
 *
 * Erst werden die Bausteine ueber den GANZEN Text gesucht, dann die Regionen einzeln mit
 * `marked.lexer` zerlegt. Frueher wurde erst an Ueberschriften geschnitten -- ein Baustein,
 * der eine Ueberschrift ueberspannt, zerfiel dabei und zeigte `[Beginn ...]` als Rohtext
 * (Befund 2026-09-24, Lauf 106003c6). Innerhalb jeder Region wird trotzdem an den
 * Abschnittsanfaengen von `schneide` geteilt (siehe `lies`), aber erst NACH der Bausteinsuche --
 * so bleibt der Baustein ganz, und kein Block reicht ueber eine Abschnittsgrenze.
 */

export type BlockArt =
  | 'ueberschrift'
  | 'absatz'
  | 'liste'
  | 'tabelle'
  | 'zitat'
  | 'code'
  | 'trenner'
  | 'html'
  | 'sonstiges';

export interface BausteinRegion {
  name: string;
  /** Beginn von `[Beginn ...]` bis Ende von `[Ende ...]`, samt Markern. */
  von: number;
  bis: number;
  /** Der Inhalt zwischen den Markern, ohne umgebenden Leerraum und `<br>`. */
  innenVon: number;
  innenBis: number;
}

export interface Block {
  index: number;
  von: number;
  bis: number;
  raw: string;
  art: BlockArt;
  /** 1-6 bei Ueberschriften, sonst 0. */
  ebene: number;
  editierbar: boolean;
  /** Index in `bausteine`, wenn der Block in einem Baustein steht. */
  baustein: number | null;
}

export interface Zerlegung {
  /** Der Eingabetext mit LF-Zeilenenden; alle Offsets beziehen sich auf ihn. */
  text: string;
  bausteine: BausteinRegion[];
  bloecke: Block[];
}

// Erkennt dieselben Baustein-Marker wie zuvor `bausteine.ts` (Task 16 entfernt, abgeloest
// durch diese strukturelle Zerlegung).
const MARKER = /\[Beginn ([^\]]{1,60})\](?:<br>)?\s*([\s\S]*?)\s*(?:<br>)?\[Ende \1\]/g;
// Der Kopf bis zum Inhalt: dasselbe gierige `\s*` wie in MARKER, deshalb derselbe Beginn.
const KOPF = /^\[Beginn [^\]]{1,60}\](?:<br>)?\s*/;

const ARTEN: Record<string, BlockArt> = {
  heading: 'ueberschrift',
  paragraph: 'absatz',
  list: 'liste',
  table: 'tabelle',
  blockquote: 'zitat',
  code: 'code',
  hr: 'trenner',
  html: 'html',
};

// Nur was die Rueckuebersetzung (`rueckuebersetzung.ts`) verlustfrei zurueckschreiben kann.
// Fuer 'liste' reicht die Zugehoerigkeit zu dieser Art nicht -- siehe `istEinfacheListe`.
const EDITIERBAR = new Set<BlockArt>(['ueberschrift', 'absatz', 'liste']);

/**
 * Ein Punkt ist einfach, wenn er hoechstens EINEN Textblock traegt (Token-Typ `text` bei einer
 * engen, `paragraph` bei einer lockeren Liste) und danach hoechstens noch GENAU EINE
 * Unterliste als letzten Token -- alles weitere (ein zweiter Absatz, ein Absatz NACH einer
 * Unterliste, Code/Zitat/Tabelle im Punkt) verlaesst die Form, die `alsMarkdown`
 * (`rueckuebersetzung.ts`) verlustfrei abdeckt.
 *
 * Ein GFM-Aufgabenpunkt (`- [x] ...`) traegt zusaetzlich `task`/`checked` -- das ignorierte
 * `punkt.task` liess so eine Liste bisher als einfach durch, die Allowlist entfernte beim
 * Rendern die Checkbox, und die Rueckuebersetzung schrieb nur noch den nackten Text zurueck:
 * der Erledigt-Status ging verloren (Codex-Fix6). Auch in Unterlisten, weil die rekursive
 * Pruefung ueber `istEinfacheListe` jeden Punkt jeder Ebene durchlaeuft.
 */
function istEinfacherPunkt(punkt: Tokens.ListItem): boolean {
  if (punkt.task === true) return false;
  let textBloecke = 0;
  let unterlisteGesehen = false;
  for (const token of punkt.tokens) {
    // Nichts darf einer Unterliste noch folgen -- sie muss der letzte Token sein.
    if (unterlisteGesehen) return false;
    if (token.type === 'text' || token.type === 'paragraph') {
      textBloecke += 1;
      if (textBloecke > 1) return false;
      continue;
    }
    if (token.type === 'list') {
      unterlisteGesehen = true;
      // `Token` schliesst `Tokens.Generic` ein (Index-Signatur, `type: string`) -- die
      // Typpruefung engt bei `token.type === 'list'` deshalb nicht auf `Tokens.List` ein.
      // Kein `any`, nur ein gezielter Cast wie beim bestehenden `ebene`-Feld unten.
      if (!istEinfacheListe(token as Tokens.List)) return false;
      continue;
    }
    // Code, Zitat, Tabelle, HTML etc. im Punkt -- ausserhalb der einfachen Form.
    return false;
  }
  return true;
}

/**
 * Plan-Entscheidung 3 (Codex-Runde 5, Auftraggeber-Entscheidung): Komplexe Listen werden nicht
 * mehr direkt bearbeitet -- nur eine EINFACHE Liste ist `editierbar`. Einfach heisst: nicht
 * locker (`loose`), und jeder Punkt ist selbst einfach (`istEinfacherPunkt`), rekursiv auch in
 * jeder Unterliste. Alles andere (lockere Listen, mehrere Absaetze je Punkt, ein Absatz nach
 * einer Unterliste, Code/Zitat/Tabelle im Punkt) bleibt lesbar, aber nicht bearbeitbar: Markieren,
 * Kommentieren und Entfernen funktionieren weiterhin fuer JEDEN Block (auch nicht-editierbare),
 * nur die direkte Bearbeitung verlangt diese Form, weil `rueckuebersetzung.ts` nur sie
 * verlustfrei zurueckschreiben kann.
 */
function istEinfacheListe(liste: Tokens.List): boolean {
  return !liste.loose && liste.items.every((punkt) => istEinfacherPunkt(punkt));
}

const KARUSSELL = 'Kacheln im Karussell';
const KACHEL_TITEL = /^ {0,3}\*\*[^*\n]+\*\*/;

/** Beginnt die (erste) Zeile mit einem fetten Titel? Dann ist sie im Karussell eine Kachel. */
export function istKachelTitel(raw: string): boolean {
  return KACHEL_TITEL.test(raw);
}

/**
 * Kachel ist nur ein Block mit fettem Titel IM Karussell. Ein fetter Satzanfang in einem anderen
 * Baustein ("Gut zu wissen") ist keine (Kleinkram P3).
 */
export function istKachel(block: Block, bausteine: readonly BausteinRegion[]): boolean {
  return (
    block.baustein !== null &&
    bausteine[block.baustein]?.name === KARUSSELL &&
    istKachelTitel(block.raw)
  );
}

/**
 * Wiederholt der erste Absatz eines Bausteins nur dessen Namen (das Modell schreibt etwa
 * `**Gut zu wissen**` ueber den Inhalt), steht der Name doppelt da: im Faehnchen und als Zeile
 * (Karte lt4ISXKygyriDJxKu9VZ). Die Darstellung blendet diesen Block aus, der Text bleibt
 * unveraendert -- Anker, Segmentierung und Export zaehlen weiter mit ihm. Fett oder nicht;
 * Varianten mit Doppelpunkt oder weiterem Text sind Inhalt und bleiben stehen.
 */
export function istDoppelteBeschriftung(block: Block, zerlegung: Zerlegung): boolean {
  if (block.baustein === null || block.art !== 'absatz') return false;
  const erster = zerlegung.bloecke.find((b) => b.baustein === block.baustein);
  const name = zerlegung.bausteine[block.baustein]?.name;
  if (erster !== block || !name) return false;
  const zeile = block.raw
    .trim()
    .replace(/^(\*\*|__)([^*_]*)\1$/, '$2')
    .trim();
  return zeile.toLowerCase() === name.toLowerCase();
}

/**
 * Kachelregel wie n8n (`Generate Ratgeber HTML`, `renderKachelnKarussell` +
 * `parseInlineBlockContent`): Im Karussell ist JEDE ZEILE, die mit einem fetten Titel
 * beginnt, eine eigene Kachel; Zeilen ohne Titel davor sind Einleitung ueber dem Raster.
 * Das Modell schreibt die Schritte oft ohne Leerzeile untereinander -- fuer `marked` ist das
 * EIN Absatz, und die Freigabeseite zeigte daraus EINE Kachel (Befund Abnahme C, Lauf
 * 106003c6). Deshalb wird ein solcher Absatz hier an jeder Titelzeile (ausser der ersten
 * Zeile) geteilt.
 *
 * Byte-Treue: Das "\n" VOR einer Titelzeile gehoert zu keinem Teil -- es bleibt in der Luecke
 * wie der Leerraum zwischen zwei Absaetzen, Invariante 4.1 haelt also. Das letzte Teilstueck
 * behaelt das Ende des Original-`raw` samt etwaigem Zeilenumbruch und verhaelt sich beim
 * Ersetzen (`alsMarkdown` haengt den Endleerraum des `raw` wieder an) genau wie heute ein
 * ganzer Absatz. Ohne Titelzeile nach der ersten Zeile bleibt der Absatz ungeteilt.
 * Liefert die Teile als `[Anfang, Ende)` relativ zu `raw`.
 */
function kachelTeile(raw: string): Array<[number, number]> {
  const teile: Array<[number, number]> = [];
  let anfang = 0;
  let umbruch = raw.indexOf('\n');
  while (umbruch !== -1) {
    const zeile = umbruch + 1;
    if (zeile < raw.length && istKachelTitel(raw.slice(zeile))) {
      teile.push([anfang, umbruch]);
      anfang = zeile;
    }
    umbruch = raw.indexOf('\n', zeile);
  }
  teile.push([anfang, raw.length]);
  return teile;
}

export function zerlegeInBloecke(eingabe: string): Zerlegung {
  // `marked` normalisiert CRLF intern; ohne dieselbe Normalisierung hier passten die
  // `raw`-Laengen nicht mehr zu den Offsets im Text.
  const text = eingabe.replace(/\r\n/g, '\n');
  const bausteine: BausteinRegion[] = [];
  const bloecke: Block[] = [];
  const anfaenge = abschnittsAnfaenge(text);

  const lexe = (von: number, bis: number, baustein: number | null) => {
    // Die Region ist vor dem Lexen schon gepusht (siehe unten), der Name also bekannt.
    const imKarussell = baustein !== null && bausteine[baustein]?.name === KARUSSELL;
    let position = von;
    for (const token of marked.lexer(text.slice(von, bis))) {
      const laenge = token.raw.length;
      if (imKarussell && token.type === 'paragraph') {
        for (const [anfang, ende] of kachelTeile(token.raw)) {
          bloecke.push({
            index: bloecke.length,
            von: position + anfang,
            bis: position + ende,
            raw: token.raw.slice(anfang, ende),
            art: 'absatz',
            ebene: 0,
            editierbar: true,
            baustein,
          });
        }
      } else if (token.type !== 'space') {
        const art = ARTEN[token.type] ?? 'sonstiges';
        bloecke.push({
          index: bloecke.length,
          von: position,
          bis: position + laenge,
          raw: token.raw,
          art,
          ebene: token.type === 'heading' ? (token as { depth: number }).depth : 0,
          editierbar:
            token.type === 'list' ? istEinfacheListe(token as Tokens.List) : EDITIERBAR.has(art),
          baustein,
        });
      }
      position += laenge;
    }
  };

  // Task 9a: Eine Region wird VOR dem Lexen an jedem Abschnittsanfang geteilt, den `schneide`
  // setzt. `marked` ist sich mit `schneide` nicht immer einig (ein HTML-Block ohne Leerzeile
  // laeuft ueber die naechste Ueberschrift, `~~~` ist fuer `marked` ein Fence, fuer `schneide`
  // nicht) -- ohne diesen Schnitt ueberspannte ein Block dann eine Abschnittsgrenze, und
  // `ersetzeBlock`/`entferneBloecke` rechneten mit einer Spanne ueber zwei Segmente. So gilt
  // die Grenze fuer jeden Text, nicht nur fuer die bekannten Faelle. Bausteine behalten ihren
  // Index in beiden Teilen.
  const lies = (von: number, bis: number, baustein: number | null) => {
    let stueckVon = von;
    for (const anfang of anfaenge) {
      if (anfang <= von || anfang >= bis) continue;
      lexe(stueckVon, anfang, baustein);
      stueckVon = anfang;
    }
    lexe(stueckVon, bis, baustein);
  };

  let zuletzt = 0;
  for (const treffer of text.matchAll(MARKER)) {
    const start = treffer.index ?? 0;
    if (start > zuletzt) lies(zuletzt, start, null);
    const kopf = KOPF.exec(treffer[0])?.[0].length ?? 0;
    const innen = treffer[2] ?? '';
    bausteine.push({
      name: treffer[1] ?? '',
      von: start,
      bis: start + treffer[0].length,
      innenVon: start + kopf,
      innenBis: start + kopf + innen.length,
    });
    const region = bausteine[bausteine.length - 1]!;
    lies(region.innenVon, region.innenBis, bausteine.length - 1);
    zuletzt = region.bis;
  }
  if (zuletzt < text.length) lies(zuletzt, text.length, null);

  return { text, bausteine, bloecke };
}
